/**
 * `clientUpcoming` — `mockups/client/dashboard.html` §4 "Upcoming" and
 * `mockups/client/report-2026-08.html`'s own "Upcoming" section, the same eight
 * items in the same order.
 *
 * `.docs/17-studio-read-models.md` §C: "the next N of: content plan items (drops),
 * report issue date, next invoice (period + estimate via 14), calls, milestones due,
 * article batches …, approved consulting sessions; each with a link target kind;
 * `tl__item--need` when the item is a decision or a gated release."
 */
import { z } from 'zod';
import { DateOnlySchema, Rfc3339Schema } from '../primitives.js';
/**
 * Where an item's own page is. `client/dashboard.html`'s `<a class="linkish">` under
 * each row: "deliverables", "the archive", "services & billing", "reschedule",
 * "project", "roadmap", "catalogue".
 */
export const CLIENT_UPCOMING_LINK_KINDS = [
    'files',
    'reports',
    'billing',
    'calls',
    'projects',
    'requests',
];
export const ClientUpcomingLinkSchema = z
    .strictObject({
    kind: z.enum(CLIENT_UPCOMING_LINK_KINDS).describe('Which page the item is about.'),
    ref: z
        .string()
        .nullable()
        .describe('The specific thing to open there — a work order ref, a call ref — or null.'),
})
    .describe('Where "the deliverables", "the archive" and so on actually point.');
export const ClientUpcomingItemSchema = z
    .strictObject({
    on: DateOnlySchema.describe('The calendar day it falls on, in the viewer’s zone (`scheduling.zoneFor`).'),
    at: Rfc3339Schema.nullable().describe('The instant it happens, for a time-of-day item such as a call — null for anything ' +
        'that is only a day, such as an invoice or a content drop. `useFormat()` renders ' +
        'the wall time and offset ("08:00 EDT"); this file returns the instant, never the ' +
        'formatted string — `dates.ts` `utcOffsetMinutes` and `BookingRulesView` are the ' +
        'same rule for the same reason.'),
    title: z.string().describe('What happens — "Content drop — 4 reels, 2 carousels".'),
    note: z.string().describe('The line under it. Empty for none.'),
    link: ClientUpcomingLinkSchema,
    need: z
        .boolean()
        .describe('`tl__item--need`: true when the item is an open decision or a handover still ' +
        'gated behind payment.'),
})
    .describe('One row of the Upcoming timeline.');
export const ClientUpcomingSchema = z
    .strictObject({
    from: DateOnlySchema.describe('The window’s first day — today, in the viewer’s zone.'),
    to: DateOnlySchema.describe('The window’s last day.'),
    items: z
        .array(ClientUpcomingItemSchema)
        .describe('Every item in the window, oldest first. Past items are excluded — ' +
        '`.docs/17-studio-read-models.md` §Edge cases — except a decision’s own passed ' +
        'deadline, which is carried through as overdue rather than dropped.'),
})
    .describe('`client/dashboard.html` §4 and `client/report-2026-08.html`’s "Upcoming".');
//# sourceMappingURL=upcoming.js.map